import { useCallback, useEffect, useRef, useState } from "react";

const CLAVE_PROPORCION = "retroia.feedback.proporcionCoach";
const MIN = 0.35;
const MAX = 0.65;
const POR_DEFECTO = 0.4;
const PASO_TECLADO = 0.05;

const limitar = (valor: number) => Math.min(MAX, Math.max(MIN, valor));

function leerProporcion(): number {
  try {
    const guardada = Number(localStorage.getItem(CLAVE_PROPORCION));
    return guardada ? limitar(guardada) : POR_DEFECTO;
  } catch {
    return POR_DEFECTO;
  }
}

function guardarProporcion(valor: number) {
  try {
    localStorage.setItem(CLAVE_PROPORCION, String(valor));
  } catch {
    // Sin almacenamiento (modo privado, etc.): se usa el valor por defecto.
  }
}

type Props = {
  izquierda: React.ReactNode;
  derecha: React.ReactNode;
  /** Con el panel derecho contraído se muestra `derechaContraida` y el panel se oculta. */
  derechaContraida: React.ReactNode;
  contraido: boolean;
  etiquetaDivisor: string;
};

/**
 * Dos paneles con un divisor arrastrable. El panel derecho ocupa entre el
 * 35 % y el 65 % del ancho; la proporción elegida se recuerda en el navegador.
 */
export function ResizableSplit({
  izquierda,
  derecha,
  derechaContraida,
  contraido,
  etiquetaDivisor,
}: Props) {
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  const [proporcion, setProporcion] = useState(leerProporcion);
  const [arrastrando, setArrastrando] = useState(false);

  useEffect(() => {
    if (!arrastrando) guardarProporcion(proporcion);
  }, [arrastrando, proporcion]);

  const moverA = useCallback((clientX: number) => {
    const rect = contenedorRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    setProporcion(limitar((rect.right - clientX) / rect.width));
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setArrastrando(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (arrastrando) moverA(e.clientX);
  };

  const terminarArrastre = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setArrastrando(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setProporcion((p) => limitar(p + PASO_TECLADO));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setProporcion((p) => limitar(p - PASO_TECLADO));
    } else if (e.key === "Home") {
      e.preventDefault();
      setProporcion(POR_DEFECTO);
    }
  };

  return (
    <div
      ref={contenedorRef}
      className={`flex flex-1 min-h-0 overflow-hidden ${arrastrando ? "select-none cursor-col-resize" : ""}`}
    >
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">{izquierda}</div>
      {contraido && derechaContraida}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={etiquetaDivisor}
        aria-valuemin={MIN * 100}
        aria-valuemax={MAX * 100}
        aria-valuenow={Math.round(proporcion * 100)}
        tabIndex={0}
        hidden={contraido}
        title="Arrastra para cambiar el tamaño"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={terminarArrastre}
        onPointerCancel={terminarArrastre}
        onKeyDown={onKeyDown}
        onDoubleClick={() => setProporcion(POR_DEFECTO)}
        className="group relative w-2 flex-shrink-0 cursor-col-resize bg-[var(--card-background)] border-l border-[var(--border)] focus-visible:outline-none"
      >
        <span
          className={`absolute left-1/2 top-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors ${
            arrastrando
              ? "bg-[var(--primary)]"
              : "bg-[var(--border)] group-hover:bg-[var(--primary)] group-focus-visible:bg-[var(--primary)]"
          }`}
        />
      </div>
      {/* El panel derecho sigue montado al contraerse para no perder el chat en curso. */}
      <div
        hidden={contraido}
        className="flex-shrink-0 min-h-0 flex flex-col"
        style={{ width: `${proporcion * 100}%` }}
      >
        {derecha}
      </div>
    </div>
  );
}
